/**
 * The one fixture for personalized distribution mocks, ported from the prototype
 * (pd/src/00-core.html and friends): Northwind Retail's "Tuesday trading review", its split
 * fields, Region stats, the 11 tiles with their left-out reasons, the sample recipient rows,
 * the built-in templates and four seeded distributions. Extend it here; don't fork it.
 */
import { formatMoneyMillions, formatSignedChange, slugOf } from "./distributionFormat";
import type {
  AudienceApi,
  LeftOutReasonApi,
  RecipientBrandApi,
  RecipientInputApi,
  SensitivityClassApi,
  SplitFieldApi,
  SplitFieldOptionApi,
  TemplateApi,
  TileKindApi,
  ValueKpiApi,
  ValueStatApi,
} from "./distributions.types";
import type { BrandKitApi } from "./distributionBrandKits.types";
import { MOCK_SCALE_FIELD_LABEL, mockScaleValues } from "./distributionsMockScale";

export const MOCK_STORY_TITLE = "Tuesday trading review";
export const MOCK_ORG = "Northwind Retail";
export const MOCK_SENDER = "Hardik Chheda";
export const MOCK_MODEL_RELEASE_ID = "rel_northwind_retail_2026_10";
/** The data behind the numbers: "from the Oct 13, 6:00 AM refresh". */
export const MOCK_DATA_AS_OF = "2026-10-13T06:00:00-07:00";
export const MOCK_DEFAULT_BRAND: RecipientBrandApi = {
  name: MOCK_ORG,
  logo_url: null,
  accent: "#2B5BA8",
  mark: "N",
};
export const MOCK_CENTRAL_COOP_BRAND: RecipientBrandApi = {
  name: "Central Co-op",
  logo_url: null,
  accent: "#0F7B6C",
  mark: "C",
};

// ─── Round 6 · brand kits (WP7) ───────────────────────────────────────────────
/** Mock-only artwork for seeded logos: a neutral shape, drawn as an SVG data URL. */
const mockLogoArt = (fill: string, ink: string): string =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="120" viewBox="0 0 480 120"><rect x="4" y="10" width="100" height="100" rx="24" fill="${fill}"/><circle cx="54" cy="60" r="22" fill="${ink}"/><rect x="128" y="44" width="300" height="32" rx="16" fill="${fill}"/></svg>`,
  )}`;

export const MOCK_BRAND_TENANT = "mock-tenant";
export const MOCK_BRAND_KIT_IDS = { standard: "kit-standard", campaign: "kit-campaign" } as const;
const mockLogoKey = (logoId: string): string => `brand-kits/${MOCK_BRAND_TENANT}/logos/${logoId}.png`;

/** Two kits: the default with a light and a dark logo and two palettes, and a second with one palette. */
export const MOCK_BRAND_KITS: BrandKitApi[] = [
  {
    id: MOCK_BRAND_KIT_IDS.standard,
    name: "Company standard",
    display_name: null,
    mark: null,
    is_default: true,
    logos: [
      { id: "logo-primary", name: "Primary logo", object_key: mockLogoKey("logo-primary"), variant: "light", content_type: "image/png", width: 1200, height: 300, url: mockLogoArt("#2B5BA8", "#FFFFFF") },
      { id: "logo-on-dark", name: "Logo on dark", object_key: mockLogoKey("logo-on-dark"), variant: "dark", content_type: "image/png", width: 1200, height: 300, url: mockLogoArt("#FFFFFF", "#2B5BA8") },
    ],
    palettes: [
      { id: "pal-primary", name: "Primary", colors: ["#2B5BA8", "#16A34A", "#F59E0B", "#7C3AED", "#0F7B6C"] },
      { id: "pal-muted", name: "Muted", colors: ["#3A4A66", "#8D9DBD", "#C6D0E1"] },
    ],
    created_by: null,
    updated_at: "2026-10-02T09:00:00Z",
  },
  {
    id: MOCK_BRAND_KIT_IDS.campaign,
    name: "Campaign",
    display_name: null,
    mark: null,
    is_default: false,
    logos: [],
    palettes: [{ id: "pal-campaign", name: "Campaign", colors: ["#B4232A", "#0F5C4A", "#E8B33A"] }],
    created_by: null,
    updated_at: "2026-10-09T09:00:00Z",
  },
];
// ─── end round 6 · brand kits ─────────────────────────────────────────────────

/** One value's numbers: sales and weekly values in $M, d = % change, gm = gross margin %, bk = basket. */
export interface MockValueStat {
  rows: number;
  sales: number;
  d: number;
  gm: number;
  gmd: number;
  bk: number;
  bkd: number;
  wk: number[];
  empty?: boolean;
}

export const MOCK_TOTAL: MockValueStat = {
  rows: 4_760_000, sales: 48.6, d: 4.2, gm: 31.8, gmd: -0.4, bk: 42.1, bkd: 1.1,
  wk: [44.1, 44.9, 45.8, 45.1, 46.4, 46.5, 46.7, 48.6],
};

export const MOCK_REGION_STATS: Record<string, MockValueStat> = {
  West: { rows: 1_420_000, sales: 14.8, d: 5.1, gm: 32.4, gmd: -0.2, bk: 44.2, bkd: 1.4, wk: [13.2, 13.6, 13.9, 13.5, 14.1, 14.0, 14.1, 14.8] },
  East: { rows: 1_180_000, sales: 12.1, d: 3.6, gm: 31.1, gmd: -0.7, bk: 41.8, bkd: 0.9, wk: [11.0, 11.2, 11.5, 11.3, 11.6, 11.7, 11.7, 12.1] },
  Central: { rows: 960_000, sales: 9.7, d: 2.9, gm: 30.9, gmd: 0.3, bk: 39.6, bkd: 0.6, wk: [8.9, 9.0, 9.2, 9.1, 9.3, 9.4, 9.4, 9.7] },
  South: { rows: 830_000, sales: 8.4, d: 6.8, gm: 32.0, gmd: -0.5, bk: 40.1, bkd: 2.2, wk: [7.2, 7.4, 7.5, 7.6, 7.7, 7.8, 7.9, 8.4] },
  International: { rows: 370_000, sales: 3.6, d: -1.2, gm: 29.4, gmd: -1.1, bk: 37.9, bkd: -0.8, wk: [3.8, 3.7, 3.7, 3.6, 3.7, 3.6, 3.6, 3.6] },
};

const STORE_NAMES = ["Union Square", "Pearl District", "Back Bay", "Lincoln Park", "Uptown Dallas", "Capitol Hill", "Mission Bay", "Midtown Atlanta"];
const BUYERS = ["Ava Brooks", "Ben Okafor", "Chloe Kim", "Diego Santos", "Elena Petrova", "Farah Haddad", "Gus Lindqvist", "Hana Sato", "Ivan Novak", "Jade Moreau", "Kofi Mensah", "Lena Fischer", "Malik Reed", "Nora Quinn", "Omar Aziz", "Paula Ruiz", "Quinn Avery", "Rhea Kapoor", "Sam Turner", "Tara Walsh", "Uma Iyer", "Victor Hale", "Wen Li", "Xavi Puig", "Yara Silva", "Zane Ford", "Amir Khan", "Bea Larsen", "Cole Hart", "Dina Vega", "Eli Shaw"];

export interface MockSplitField {
  label: string;
  plural: string;
  sourceRef: string;
  description: string;
  values: string[];
  /** Values with no data this week. */
  empty: string[];
  /** The column's class; above an audience's clearance, the field can't split its copies. */
  sensitivity: SensitivityClassApi;
}

export const MOCK_FIELDS: MockSplitField[] = [
  { label: "Region", plural: "Regions", sourceRef: "dim_store.region", description: "Sales region the store belongs to. Good for regional leadership packs.", values: ["West", "East", "Central", "South", "International"], empty: [], sensitivity: "internal" },
  { label: "District", plural: "Districts", sourceRef: "dim_store.district", description: "Group of stores run by one district manager.", values: ["Bay Area", "Pacific NW", "SoCal", "Mountain", "Great Lakes", "Tri-State", "New England", "Mid-Atlantic", "Texas", "Southeast", "Florida", "Plains", "Ohio Valley", "Gulf Coast", "Canada", "UK & Ireland", "DACH", "Benelux"], empty: [], sensitivity: "internal" },
  { label: "Store", plural: "Stores", sourceRef: "dim_store.store_name", description: "A single store. One copy for each store manager.", values: Array.from({ length: 126 }, (_, i) => `Store ${String(i + 1).padStart(3, "0")}${i < 8 ? ` · ${STORE_NAMES[i]}` : ""}`), empty: [], sensitivity: "internal" },
  { label: "Category", plural: "Categories", sourceRef: "dim_product.category", description: "Product category. Good for buyer and category reviews.", values: ["Grocery", "Fresh", "Frozen", "Beverages", "Health & beauty", "Home", "Apparel", "Electronics", "Seasonal"], empty: ["Seasonal"], sensitivity: "public" },
  { label: "Customer segment", plural: "Customer segments", sourceRef: "dim_customer.loyalty_tier", description: "Loyalty tier of the shopper.", values: ["Loyalty Gold", "Loyalty Silver", "New shoppers", "Lapsed"], empty: [], sensitivity: "confidential" },
  { label: "Buyer", plural: "Buyers", sourceRef: "dim_product.buyer", description: "Merchandise buyer who owns the category.", values: BUYERS, empty: [], sensitivity: "restricted_pii" },
  // Appended for People at scale (192 values): see distributionsMockScale.ts.
  { label: MOCK_SCALE_FIELD_LABEL, plural: "Territories", sourceRef: "dim_store.territory", description: "A group of stores run by one territory manager.", values: mockScaleValues(), empty: [], sensitivity: "internal" },
];

export const mockFieldByLabel = (label: string | null | undefined): MockSplitField | null =>
  MOCK_FIELDS.find((field) => field.label === label) ?? null;

export const toMockSplitField = (field: MockSplitField): SplitFieldApi => ({
  field: { kind: "dimension_attribute", model_field_id: `fld_${slugOf(field.label).replace(/-/g, "_")}` },
  model_release_id: MOCK_MODEL_RELEASE_ID,
  label: field.label,
  description: field.description,
  source_ref: field.sourceRef,
});

export const MOCK_SPLIT_FIELDS: SplitFieldApi[] = MOCK_FIELDS.map(toMockSplitField);
export const MOCK_REGION_SPLIT: SplitFieldApi = MOCK_SPLIT_FIELDS[0];

/** The tenant defaults: people inside may see confidential values, people outside internal ones. */
export const MOCK_AUDIENCE_CLEARANCE: Record<AudienceApi, SensitivityClassApi> = { inside: "confidential", outside: "internal" };
const SENSITIVITY_ORDER: readonly SensitivityClassApi[] = ["public", "internal", "confidential", "restricted_pii"];
const SENSITIVITY_NAME: Record<SensitivityClassApi, string> = {
  public: "public",
  internal: "internal",
  confidential: "confidential",
  restricted_pii: "restricted personal data",
};

/** Whether a field's values are above what `audience` may see (magic-core `SensitivityClass.exceeds`). */
export const mockAboveClearance = (field: MockSplitField, audience: AudienceApi): boolean =>
  SENSITIVITY_ORDER.indexOf(field.sensitivity) > SENSITIVITY_ORDER.indexOf(MOCK_AUDIENCE_CLEARANCE[audience]);

/** The server's reason a field can't split for `audience`, verbatim. */
export const mockBlockedReason = (field: MockSplitField, audience: AudienceApi): string =>
  `${field.label} is classed ${SENSITIVITY_NAME[field.sensitivity]}, so copies for people ${audience === "inside" ? "in" : "outside"} the organisation can’t show its values. Pick another field.`;

/** A field as GET /split-fields?audience= lists it. */
export const toMockSplitFieldOption = (field: MockSplitField, audience: AudienceApi): SplitFieldOptionApi => {
  const above = mockAboveClearance(field, audience);
  return {
    ...toMockSplitField(field),
    sensitivity_class: field.sensitivity,
    above_clearance: above,
    blocked_reason: above ? mockBlockedReason(field, audience) : null,
    display_label: field.label,
  };
};

export interface MockTile {
  id: string;
  title: string;
  kind: TileKindApi;
  sensitive: boolean;
  /** Left out of every copy, whatever the split. */
  leftOut?: { reason: LeftOutReasonApi; message: string; canOptIn: boolean };
}

export const MOCK_TILES: MockTile[] = [
  { id: "k1", title: "Net sales", kind: "kpi", sensitive: false },
  { id: "k2", title: "Gross margin", kind: "kpi", sensitive: true },
  { id: "k3", title: "Avg basket", kind: "kpi", sensitive: false },
  { id: "c1", title: "Net sales by week", kind: "chart", sensitive: false },
  { id: "c2", title: "Sales by category", kind: "chart", sensitive: false },
  { id: "c3", title: "Top 5 stores", kind: "chart", sensitive: false },
  { id: "t1", title: "Store league table", kind: "table", sensitive: false },
  { id: "e1", title: "Inventory heatmap", kind: "bi_view", sensitive: false, leftOut: { reason: "bi_view", message: "MagicBI can’t filter an embedded Tableau view, so it would show every slice’s data.", canOptIn: false } },
  { id: "i1", title: "Promo calendar", kind: "still", sensitive: false, leftOut: { reason: "still", message: "A captured image is a picture of the data. It can’t be filtered.", canOptIn: true } },
  { id: "n1", title: "Ops commentary", kind: "note", sensitive: false, leftOut: { reason: "static_item", message: "Notes read the same for everyone, so they’re left out unless you include them.", canOptIn: true } },
  { id: "l1", title: "Pricing playbook", kind: "link", sensitive: false, leftOut: { reason: "static_item", message: "Links open the same page for everyone, so they’re left out unless you include them.", canOptIn: true } },
];

/** Tiles a particular split can't take (prototype FIELD_BLOCK). */
export const MOCK_FIELD_BLOCKS: Record<string, Record<string, { reason: LeftOutReasonApi; message: string }>> = {
  Store: { c3: { reason: "not_drawable", message: "Ranks stores against each other, so one store’s copy can’t show it." } },
  Category: { k3: { reason: "not_reachable", message: "Basket size is measured per checkout, not per category." } },
};

// Round 6 · step 1 items (FE-items)
/** Story Manager's message on a tile the owner turned off (reason `turned_off`). */
export const MOCK_TURNED_OFF_MESSAGE = "Turned off in Split by, so it’s left out of every copy.";

export const MOCK_OPS_NOTE ="Strong week for South after the promo reset. West margin dipped on apparel clearance; expect it to recover by W43.";
export const MOCK_PLAYBOOK_URL = "https://confluence.northwind.com/pricing/fall-2026";

/** The prototype's `stat(field, value)`: Region from the table, other fields from a stable formula. */
export const mockStatFor = (fieldLabel: string, value: string): MockValueStat => {
  if (fieldLabel === "Region" && MOCK_REGION_STATS[value]) return MOCK_REGION_STATS[value];
  const field = mockFieldByLabel(fieldLabel) ?? MOCK_FIELDS[0];
  if (field.empty.includes(value)) {
    return { rows: 0, sales: 0, d: 0, gm: 0, gmd: 0, bk: 0, bkd: 0, wk: [0, 0, 0, 0, 0, 0, 0, 0], empty: true };
  }
  const i = Math.max(0, field.values.indexOf(value));
  const n = field.values.length;
  const w = 0.6 + ((i * 53) % 9) / 10;
  const sales = (48.6 / n) * w;
  const wk = Array.from({ length: 8 }, (_, k) => +(sales * (0.9 + ((k * 7 + i * 3) % 11) / 100 + k * 0.008)).toFixed(3));
  wk[7] = +sales.toFixed(3);
  return {
    rows: Math.round((4_760_000 / n) * w), sales, d: (((i * 29) % 13) - 4) / 2, gm: 30 + ((i * 17) % 30) / 10,
    gmd: (((i * 7) % 9) - 4) / 10, bk: 38 + ((i * 11) % 80) / 10, bkd: (((i * 5) % 7) - 2) / 2, wk,
  };
};

const direction = (v: number): ValueKpiApi["direction"] => (v > 0 ? "up" : v < 0 ? "down" : "flat");

/** A value as POST /value-stats reports it. Gross margin is sensitive, so it's masked for everyone. */
export const mockValueStat = (fieldLabel: string, value: string): ValueStatApi => {
  const s = mockStatFor(fieldLabel, value);
  const basketLeftOut = Boolean(MOCK_FIELD_BLOCKS[fieldLabel]?.k3);
  const kpis: ValueKpiApi[] = [
    { label: "Net sales", formatted: formatMoneyMillions(s.sales), delta_formatted: formatSignedChange(s.d, "%"), direction: direction(s.d), masked: false },
    { label: "Gross margin", formatted: null, delta_formatted: null, direction: null, masked: true },
  ];
  if (!basketLeftOut) {
    kpis.push({ label: "Avg basket", formatted: `$${s.bk.toFixed(2)}`, delta_formatted: formatSignedChange(s.bkd, "%"), direction: direction(s.bkd), masked: false });
  }
  return { value, rows: s.rows, kpis: s.empty ? [] : kpis, trend: s.empty ? null : s.wk };
};

const row = (position: number, partial: Partial<RecipientInputApi> & Pick<RecipientInputApi, "name" | "value" | "address">): RecipientInputApi => ({
  position,
  channel: "email",
  cc: [],
  formats: null,
  template_id: null,
  theme_id: null,
  brand: null,
  extra: {},
  source: "csv",
  ...partial,
});

/**
 * The prototype's `loadSample()` rows (regional-pack-recipients.csv). Four have problems as in
 * the prototype (an email with no ending, a Slack channel without the app, "Pacific" which isn't
 * a Region, a duplicate); the Teams row needs a fix because its address is not a webhook link.
 */
export const MOCK_SAMPLE_ROWS: RecipientInputApi[] = [
  row(1, { name: "Maya Chen", value: "West", address: "maya.chen@northwind.com", formats: ["pdf", "pptx"], extra: { team: "West field ops" } }),
  row(2, { name: "West leadership", value: "West", channel: "slack", address: "#west-leadership", formats: ["pdf"], extra: { team: "West field ops" } }),
  row(3, { name: "Priya Natarajan", value: "East", address: "priya.natarajan@northwind", formats: ["pdf"], extra: { team: "East ops" } }),
  row(4, { name: "East leads", value: "East", channel: "teams", address: "Sales › East leads", formats: ["pdf"], extra: { team: "East ops" } }),
  row(5, { name: "Daniel Reyes", value: "Central", address: "daniel.reyes@centralcoop.com", formats: ["pptx"], template_id: "qbr", cc: ["ops@northwind.com"], brand: MOCK_CENTRAL_COOP_BRAND, extra: { team: "Central franchise" } }),
  row(6, { name: "South region", value: "South", channel: "slack", address: "#south-region", formats: ["pdf"], extra: { team: "South ops" } }),
  row(7, { name: "Marcus Webb", value: "Pacific", address: "marcus.webb@northwind.com", formats: ["pdf"], extra: { team: "Pacific pilot" } }),
  row(8, { name: "Maya Chen", value: "West", address: "maya.chen@northwind.com", formats: ["pdf"], extra: { team: "West field ops" } }),
];

export const MOCK_SAMPLE_CSV_NAME = "regional-pack-recipients.csv";

/** The sample rows as the uploaded CSV, for "Use the sample file". */
export const MOCK_SAMPLE_CSV = [
  "region,full_name,channel,address,formats,template,cc,logo_url,accent,team",
  "West,Maya Chen,email,maya.chen@northwind.com,pdf+pptx,,,,,West field ops",
  "West,West leadership,slack,#west-leadership,pdf,,,,,West field ops",
  "East,Priya Natarajan,email,priya.natarajan@northwind,pdf,,,,,East ops",
  "East,East leads,teams,Sales › East leads,pdf,,,,,East ops",
  "Central,Daniel Reyes,email,daniel.reyes@centralcoop.com,pptx,QBR deck,ops@northwind.com,,#0F7B6C,Central franchise",
  "South,South region,slack,#south-region,pdf,,,,,South ops",
  "Pacific,Marcus Webb,email,marcus.webb@northwind.com,pdf,,,,,Pacific pilot",
  "West,Maya Chen,email,maya.chen@northwind.com,pdf,,,,,West field ops",
].join("\r\n");

/** Slack channels the MagicBI app has been removed from (prototype `slackMissing`). */
export const MOCK_SLACK_CHANNELS_WITHOUT_APP = ["#south-region"];

/** The weekly regional pack's people: the prototype's run roster, on live channels only. */
export const MOCK_REGIONAL_PACK_ROWS: RecipientInputApi[] = [
  row(1, { name: "Maya Chen", value: "West", address: "maya.chen@northwind.com", cc: ["west-ops@northwind.com"], formats: ["pdf", "pptx"], extra: { team: "West field ops" } }),
  row(2, { name: "West leadership", value: "West", channel: "slack", address: "#west-leadership", formats: ["pdf"], extra: { team: "West field ops" } }),
  row(3, { name: "Priya Natarajan", value: "East", address: "priya.natarajan@northwind.com", formats: ["pdf"], extra: { team: "East ops" } }),
  row(4, { name: "East leads", value: "East", channel: "slack", address: "#east-leads", formats: ["pdf"], extra: { team: "East sales" } }),
  row(5, { name: "Daniel Reyes", value: "Central", address: "daniel.reyes@centralcoop.com", formats: ["pptx"], template_id: "qbr", brand: MOCK_CENTRAL_COOP_BRAND, extra: { team: "Central franchise" } }),
  row(6, { name: "South region", value: "South", channel: "slack", address: "#south-region", formats: ["pdf"], extra: { team: "South ops" } }),
  row(7, { name: "Luis Ortega", value: "International", address: "luis.ortega@northwind.com", formats: ["pdf", "pptx"], extra: { team: "" } }),
  row(8, { name: "International leads", value: "International", channel: "slack", address: "#international-leads", formats: ["pdf"], extra: { team: "International" } }),
];

export const MOCK_CATEGORY_ROWS: RecipientInputApi[] = [
  ["Ava Brooks", "Grocery", "email", "ava.brooks@northwind.com"],
  ["Grocery buyers", "Grocery", "slack", "#grocery-buyers"],
  ["Ben Okafor", "Fresh", "email", "ben.okafor@northwind.com"],
  ["Chloe Kim", "Frozen", "email", "chloe.kim@northwind.com"],
  ["Diego Santos", "Beverages", "email", "diego.santos@northwind.com"],
  ["Elena Petrova", "Health & beauty", "email", "elena.petrova@northwind.com"],
  ["Farah Haddad", "Home", "email", "farah.haddad@northwind.com"],
  ["Ivan Novak", "Home", "email", "ivan.novak@northwind.com"],
  ["Gus Lindqvist", "Apparel", "email", "gus.lindqvist@northwind.com"],
  ["Apparel team", "Apparel", "slack", "#apparel-team"],
  ["Hana Sato", "Electronics", "email", "hana.sato@northwind.com"],
].map(([name, value, channel, address], i) =>
  row(i + 1, { name, value, channel: channel as RecipientInputApi["channel"], address, formats: ["pdf"], extra: { team: `${value} buying` } }),
);

const FIRST = ["Aisha", "Ben", "Carmen", "Dev", "Elif", "Felix", "Grace", "Hiro", "Ines", "Jonah", "Kira", "Luis", "Mei", "Nate", "Olga", "Pavel", "Rosa", "Sanjay", "Tess", "Umar", "Vera", "Wes", "Yusuf", "Zoe"];
const LAST = ["Patel", "Romero", "Okafor", "Lindgren", "Nakamura", "Byrne", "Haddad", "Costa", "Mueller", "Owusu", "Silva", "Tanaka", "Whitfield", "Kowalski", "Mensah", "Duarte", "Ahmed"];

/** One manager per store, for the completed Q3 review (126 rows, two pages at limit 100). */
export const MOCK_STORE_ROWS: RecipientInputApi[] = MOCK_FIELDS[2].values.map((value, i) => {
  const name = `${FIRST[(i * 5 + 3) % FIRST.length]} ${LAST[(i * 7 + 1) % LAST.length]}`;
  return row(i + 1, { name, value, address: `${name.toLowerCase().replace(" ", ".")}@northwind.com`, formats: ["pdf"], extra: { team: "Store team" } });
});

const BUILT_IN = (partial: Omit<TemplateApi, "source" | "master_file_name" | "created_by" | "updated_at">): TemplateApi => ({
  ...partial,
  source: "built_in",
  master_file_name: null,
  created_by: null,
  updated_at: null,
});

/** The four built-ins, in the prototype's order. Story layout has one slide per personalized tile. */
export const MOCK_BUILT_IN_TEMPLATES: TemplateApi[] = [
  BUILT_IN({ id: "story", name: "Story layout", kind: "deck", description: "One slide per personalized tile, in story order", slide_count: 7, slides_spec: null }),
  // Same slides as magic-core BUILT_IN_TEMPLATES (contracts.md §3): tile slots are null and filled
  // with the first unused tiles of their type when a copy is made.
  BUILT_IN({
    id: "qbr", name: "QBR deck", kind: "deck", description: "Cover, summary, KPIs, two charts, a comparison, the detail and a takeaway", slide_count: 8,
    slides_spec: [
      { title: "Cover", layout: "cover", slots: [{ kind: "cover" }] },
      { title: "Summary", layout: "summary", slots: [{ kind: "ai_summary" }] },
      { title: "Key numbers", layout: "kpi3", slots: [null, null, null] },
      { title: "Trend", layout: "chart1", slots: [null] },
      { title: "Mix", layout: "chart1", slots: [null] },
      { title: "Comparison", layout: "chart2", slots: [null, null] },
      { title: "Detail", layout: "table", slots: [null] },
      { title: "Takeaway", layout: "charttext", slots: [null, { kind: "ai_summary" }] },
    ],
  }),
  BUILT_IN({
    id: "weekly", name: "Executive weekly", kind: "deck", description: "Cover, the numbers, this week, a watch list and next steps", slide_count: 5,
    slides_spec: [
      { title: "Cover", layout: "cover", slots: [{ kind: "cover" }] },
      { title: "The numbers", layout: "kpi3", slots: [null, null, null] },
      { title: "This week", layout: "chart1", slots: [null] },
      { title: "Watch list", layout: "charttext", slots: [null, { kind: "ai_summary" }] },
      { title: "Next steps", layout: "summary", slots: [{ kind: "ai_summary" }] },
    ],
  }),
  BUILT_IN({
    id: "memo", name: "One-page memo", kind: "memo", description: "Headline, AI summary, KPI row and one chart", slide_count: 1,
    slides_spec: [
      { title: "Headline", layout: "cover", slots: [{ kind: "cover" }] },
      { title: "AI summary", layout: "summary", slots: [{ kind: "ai_summary" }] },
      { title: "KPI row", layout: "kpi3", slots: [null, null, null] },
      { title: "One chart", layout: "chart1", slots: [null] },
    ],
  }),
];

/** A new distribution's message: built-in tokens only, so it never blocks activation. */
export const MOCK_NEW_DISTRIBUTION_MESSAGE = {
  email_subject: "{{value}} · {{story}} for {{date}}",
  email_body:
    "Hi {{name}},\n\nHere’s {{story}} for {{value}}. Everything below is filtered to {{value}}, and the full copy is attached.\n\nThanks,\n{{sender}}",
  slack_text: "Hi {{name}}, here’s {{story}} for {{value}}. The full copy is attached.",
};

/** The prototype's step 3 message, with the sample CSV's {{team}} column. */
export const MOCK_DEFAULT_MESSAGE = {
  email_subject: "{{value}} · {{story}} for {{date}}",
  email_body:
    "Hi {{name}},\n\nHere’s {{story}} for {{value}}, prepared for {{team}}. Everything below is filtered to {{value}}, and the full copy is attached.\n\nThanks,\n{{sender}}",
  slack_text: "Hi {{name}}, here’s {{story}} for {{value}}. The full copy is attached.",
};

/** Theme ids the mock accepts on rows (the presentation-themes mock's keys). */
export const MOCK_THEME_IDS = ["magicbi", "contoso", "magicbi-dark"];

// ─── Round 6 · WP5 recipients from the data model (FE-recipients) ─────────────

/** The mock caller's own clearance: model attributes above it come back masked. */
export const MOCK_CALLER_CLEARANCE: SensitivityClassApi = "confidential";

/** An attribute a recipient column can be read from, in one of the mock model's tables. */
export interface MockModelAttribute {
  id: string;
  table: string;
  label: string;
  description: string | null;
  sensitivity: SensitivityClassApi;
  /** The record key it reads; the split field Region reads each person's `region`. */
  key: keyof MockPerson;
}

/** One person in the mock model (an employee or an account contact). */
export interface MockPerson {
  table: string;
  name: string;
  email: string;
  slack: string;
  team: string;
  title: string;
  status: "active" | "inactive";
  region: string;
  account: string;
  accent: string;
  home: string;
}

const EMPLOYEE = "Employee";
const CONTACT = "Account contact";

export const MOCK_MODEL_ATTRIBUTES: MockModelAttribute[] = [
  { id: "fld_employee_name", table: EMPLOYEE, label: "Name", description: "The person’s full name.", sensitivity: "internal", key: "name" },
  { id: "fld_employee_email", table: EMPLOYEE, label: "Work email", description: null, sensitivity: "internal", key: "email" },
  { id: "fld_employee_slack", table: EMPLOYEE, label: "Slack channel", description: "Their team’s channel.", sensitivity: "internal", key: "slack" },
  { id: "fld_employee_region", table: EMPLOYEE, label: "Region", description: "The region they look after.", sensitivity: "internal", key: "region" },
  { id: "fld_employee_team", table: EMPLOYEE, label: "Team", description: null, sensitivity: "internal", key: "team" },
  { id: "fld_employee_title", table: EMPLOYEE, label: "Title", description: null, sensitivity: "internal", key: "title" },
  { id: "fld_employee_status", table: EMPLOYEE, label: "Status", description: "active or inactive.", sensitivity: "internal", key: "status" },
  { id: "fld_employee_home", table: EMPLOYEE, label: "Home address", description: null, sensitivity: "restricted_pii", key: "home" },
  { id: "fld_contact_name", table: CONTACT, label: "Contact name", description: null, sensitivity: "internal", key: "name" },
  { id: "fld_contact_email", table: CONTACT, label: "Email", description: null, sensitivity: "internal", key: "email" },
  { id: "fld_contact_region", table: CONTACT, label: "Account region", description: null, sensitivity: "internal", key: "region" },
  { id: "fld_contact_account", table: CONTACT, label: "Account name", description: null, sensitivity: "internal", key: "account" },
  { id: "fld_contact_accent", table: CONTACT, label: "Brand accent", description: "The account’s brand colour.", sensitivity: "public", key: "accent" },
];

const person = (table: string, over: Partial<MockPerson>): MockPerson => ({
  table, name: "", email: "", slack: "", team: "", title: "", status: "active", region: "", account: "", accent: "", home: "", ...over,
});

const employee = (name: string, region: string, title: string, slack: string, over: Partial<MockPerson> = {}): MockPerson =>
  person(EMPLOYEE, {
    name, region, title, slack, team: `${region} team`,
    email: `${name.toLowerCase().replace(/[^a-z ]/g, "").trim().replace(/\s+/g, ".")}@northwind.com`,
    home: `${region} street`, ...over,
  });

export const MOCK_MODEL_PEOPLE: MockPerson[] = [
  employee("Maya Chen", "West", "Regional VP", "#west-leadership"),
  employee("Jonah Byrne", "West", "Ops lead", "#west-ops"),
  employee("Tess Brennan", "West", "Analyst", ""),
  employee("Priya Natarajan", "East", "Regional VP", "#east-leads"),
  employee("Luis Costa", "East", "Ops lead", ""),
  employee("Grace Owusu", "Central", "Regional VP", "#central-leads"),
  employee("Marcus Webb", "Central", "Analyst", ""),
  employee("Rosa Tanaka", "South", "Regional VP", "#south-region"),
  employee("Dev Mensah", "South", "Ops lead", "#south-ops", { status: "inactive" }),
  employee("Ines Duarte", "International", "Regional VP", "#intl-leads"),
  employee("Hiro Silva", "International", "Ops lead", "", { email: "" }),
  person(CONTACT, { name: "Daniel Reyes", email: "daniel.reyes@centralcoop.com", region: "Central", account: "Central Co-op", accent: "#0F7B6C" }),
  person(CONTACT, { name: "Ava Brooks", email: "ava.brooks@harborfoods.com", region: "West", account: "Harbor Foods", accent: "#0F7B6C" }),
  person(CONTACT, { name: "Omar Aziz", email: "omar.aziz@summitgrocers.com", region: "East", account: "Summit Grocers", accent: "#B4472E" }),
  person(CONTACT, { name: "Paula Ruiz", email: "paula.ruiz@gulfmart.com", region: "South", account: "Gulf Mart", accent: "#B7791F" }),
];

/** The tenant's active members: their email domains are the "inside" domains (§4.1). */
export const MOCK_MEMBER_EMAILS = [
  ...MOCK_MODEL_PEOPLE.filter((p) => p.table === EMPLOYEE && p.status === "active" && p.email).map((p) => p.email),
  "you@northwind.com",
];

const aboveCaller = (sensitivity: SensitivityClassApi): boolean =>
  SENSITIVITY_ORDER.indexOf(sensitivity) > SENSITIVITY_ORDER.indexOf(MOCK_CALLER_CLEARANCE);

const callerBlockedReason = (label: string, sensitivity: SensitivityClassApi): string =>
  `${label} is classed ${SENSITIVITY_NAME[sensitivity]}, above what you may see yourself: its values come back hidden.`;

/** An attribute as GET /model-fields lists it (relative to the mock caller's clearance). */
export const toMockModelFieldOption = (attribute: MockModelAttribute): SplitFieldOptionApi => ({
  field: { kind: "dimension_attribute", model_field_id: attribute.id },
  model_release_id: MOCK_MODEL_RELEASE_ID,
  label: attribute.label,
  description: attribute.description,
  source_ref: `${attribute.table}.${attribute.label}`,
  sensitivity_class: attribute.sensitivity,
  above_clearance: aboveCaller(attribute.sensitivity),
  blocked_reason: aboveCaller(attribute.sensitivity) ? callerBlockedReason(attribute.label, attribute.sensitivity) : null,
  display_label: attribute.label,
});

/** A split candidate as GET /model-fields lists it. */
export const toMockModelSplitFieldOption = (field: MockSplitField): SplitFieldOptionApi => ({
  ...toMockSplitField(field),
  sensitivity_class: field.sensitivity,
  above_clearance: aboveCaller(field.sensitivity),
  blocked_reason: aboveCaller(field.sensitivity) ? callerBlockedReason(field.label, field.sensitivity) : null,
  display_label: field.label,
});

/** How the mock reads a model field: its table (null: any), the person key, and whether it's masked. */
export const mockModelFieldReader = (
  fieldId: string,
): { table: string | null; key: keyof MockPerson | null; masked: boolean } | null => {
  const attribute = MOCK_MODEL_ATTRIBUTES.find((entry) => entry.id === fieldId);
  if (attribute) return { table: attribute.table, key: attribute.key, masked: aboveCaller(attribute.sensitivity) };
  const split = MOCK_FIELDS.find((field) => toMockSplitField(field).field.model_field_id === fieldId);
  if (!split) return null;
  // Every person in the mock model belongs to a Region; the other split fields don't reach people.
  return { table: null, key: split.label === "Region" ? "region" : null, masked: aboveCaller(split.sensitivity) };
};
